import React, { useEffect, useState } from 'react';

import introImg from '../../../../assets/images/courses/teamplayer&collaboration/chapter11.1.jpg';
import actionBackground from '../../../../assets/images/courses/teamplayer&collaboration/chapter11.2.jpg';
import chapterImg4 from '../../../../assets/images/courses/teamplayer&collaboration/chapter11.3.jpg';
import {
  course_chat_questions,
  course_chat_questions2,
  course_questions as courseQuestions,
} from '../../../../courses/building-teamwork-collaboration/building-teamwork-collaboration-quiz';
import { CourseChapter } from '../../../../courses/courses.types';
import {
  CourseQuestionAnswer,
  QuestionOption,
} from '../../../../courses/jumpstart-communicator/jumpstart-communicator.types';
import { useUserCourseQuestionResponseContext } from '../../../../hooks/use-user-course-question-response.hooks';
import {
  dispatchUpdateUserCourseQuestionResponse,
  UserChapterQuestion,
} from '../../../../redux/slice/user-course-question-response';
import { useAppDispatch } from '../../../../redux/store';
import { ActionableBlock } from '../../ActionableBlock';
import { CourseQuestionCard } from '../../CourseQuestionCard';
import { MediaBlock } from '../../MediaBlock';

interface TeamPlayersAndCollaborationChapter1 {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

type AnswersState = {
  [question: string]: CourseQuestionAnswer;
};

export const TeamPlayersAndCollaborationChapter11: React.FC<
  TeamPlayersAndCollaborationChapter1
> = ({ chapter, onNextChapter, totalPages }) => {
  const dispatch = useAppDispatch();
  const userCourseQuestionResponseContext = useUserCourseQuestionResponseContext();
  const [, setAnswers] = useState<AnswersState>({});
  const [isPageDirty, setIsPageDirty] = useState(true);
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };
  const chapterQuestions = courseQuestions.filter(
    (courseChapter) => courseChapter.chapter_id === chapter.id,
  );

  const handleCourseAnswerSubmit = async (
    quizAnswers: QuestionOption[],
    isCorrect: boolean,
    questionId?: string,
  ) => {
    setAnswers((prevAnswers) => {
      if (questionId) {
        return {
          ...prevAnswers,
          [questionId]: {
            chapter_id: chapter.id,
            question_id: questionId,
            question: chapterQuestions[0].question,
            is_correct: isCorrect,
            answer_options: quizAnswers,
          },
        };
      } else {
        return { ...prevAnswers };
      }
    });

    const userQuestionAnswer: UserChapterQuestion = {
      question_id: questionId,
      isCorrect: isCorrect,
      question: chapterQuestions.find((x) => x.id === questionId)?.question,
      answers: quizAnswers,
    };
    const userAnswerResponse =
      userCourseQuestionResponseContext[chapterQuestions[0].course_id]?.chapters[chapter.id]
        ?.questions || [];

    await dispatch(
      dispatchUpdateUserCourseQuestionResponse({
        [chapterQuestions[0].course_id]: {
          chapters: {
            [chapter.id]: {
              questions: [
                ...userAnswerResponse.filter((x) => x.question_id !== questionId),
                userQuestionAnswer,
              ],
              quiz: null,
            },
          },
        },
      }),
    );
  };

  const handleQuestionRetake = async (questionId: string) => {
    setAnswers((prevAnswers) => {
      //eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [questionId]: _, ...newState } = prevAnswers;
      return newState;
    });
    const userAnswerResponse =
      userCourseQuestionResponseContext[chapterQuestions[0].course_id]?.chapters[chapter.id]
        ?.questions || [];

    await dispatch(
      dispatchUpdateUserCourseQuestionResponse({
        [chapterQuestions[0].course_id]: {
          chapters: {
            [chapter.id]: {
              questions: [...userAnswerResponse.filter((x) => x.question_id !== questionId)],
              quiz: null,
            },
          },
        },
      }),
    );
    setIsPageDirty(true);
  };

  useEffect(() => {
    const useCourseQuestionResponse =
      userCourseQuestionResponseContext[chapterQuestions[0].course_id]?.chapters[chapter.id]
        ?.questions || [];

    if (
      Object.keys(userCourseQuestionResponseContext).length &&
      useCourseQuestionResponse?.length
    ) {
      setIsPageDirty(false);
    }
  }, [userCourseQuestionResponseContext]);
  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Avoid Risky Shift & Groupthink</h2>
        <br />
        <MediaBlock mediaImage={introImg}></MediaBlock>
        <br />
        <p className="text-2xl text-center font-light mt-10 mb-10">
          In teamwork, it's crucial to watch out for two tricky issues: risky shift and groupthink.
          Both risky shift and groupthink can lead to big problems down the line if they're not
          addressed.
        </p>
        <br />
        <p className="text-lg text-center font-semibold mt-10 mb-10">
          Let's have a quick look at both of these issues in more detail, why they happen, whether
          they matter, and what can be done if they do happen.
        </p>
        <h3 className="text-3xl text-purple-600 font-bold mt-10 m-5">Risky Shift</h3>
        <p className="text-lg text-center font-normal mt-10 mb-10">
          Risky shift refers to teams making riskier decisions than individuals would on their own.
          This happens because in a team, there's less individual accountability if things go wrong,
          and cautious team members may not speak up as much.
        </p>
        <br />
        <h2 className="font-bold text-3xl">Does it matter? - Absolutely</h2>
        <br />
        <h2 className="font-semibold text-xl">Risky Shift Scenario Example: </h2>
        <ActionableBlock
          backgroundImage={actionBackground}
          courseChatQuestions={course_chat_questions}
          characterImage={'charIntro'}
          feedbackTitle="Scenario Complete!"
          feedbackText=" "
          buttonName="Continue"
          cssClass="perception-chat"
        />
        <CourseQuestionCard
          submittedAnswer={userCourseQuestionResponseContext[
            chapterQuestions[0].course_id
          ]?.chapters[chapter.id]?.questions?.find((x) => x.question_id === chapterQuestions[0].id)}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmitAnswer={handleCourseAnswerSubmit}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onRetakeQuestion={handleQuestionRetake}
          question={chapterQuestions[0]}
        />
        <h2 className="font-semibold text-2xl mt-7 mb-7">
          In short, because everyone's feeling confident and supportive of the risky idea, they end
          up deciding to take the chance, even though it might not have been the safest choice if
          they had thought about it more carefully as individuals.{' '}
        </h2>
        <br />
        <h2 className="font-semibold text-xl mt-7">Preventing Risky Shift</h2>
        <br />
        <p className="text-lg text-left font-light mt-10 mb-10">
          Making overly risky decisions can lead to serious consequences. That's why it's crucial to
          ensure decisions are made carefully and not just by a group of people feeling gung-ho.
          Always make sure someone takes responsibility for each decision.
        </p>
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Next we'll look at an issue that can arise when teams start thinking alike. Click
          continue.
        </p>
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Groupthink</h3>
        <p className="text-lg text-left font-light mt-10 mb-10">
          Groupthink occurs when teams all start thinking alike, and nobody questions the plan until
          it's too late. It's like having a cultural way of thinking in your company that everyone
          follows without realizing it. People may keep quiet about potential problems because they
          don't want to seem negative or different.
        </p>

        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg4} alt="" />
          </div>
          <p className="flex-1">
            <br />
            Interestingly, it's often the quiet ones who have valuable thoughts, but they only speak
            up afterwards, saying, "I was worried about that." And when asked why they didn't say
            anything earlier, they respond, "You didn't ask."
          </p>
        </div>

        <h2 className="font-semibold text-xl">Groupthink Scenario Example:</h2>
        <br />
        <ActionableBlock
          backgroundImage={actionBackground}
          courseChatQuestions={course_chat_questions2}
          characterImage={'charIntro'}
          feedbackTitle="Scenario Complete!"
          feedbackText=" "
          buttonName="Continue"
          cssClass="perception-chat"
        />

        <br />
        <CourseQuestionCard
          submittedAnswer={userCourseQuestionResponseContext[
            chapterQuestions[0].course_id
          ]?.chapters[chapter.id]?.questions?.find((x) => x.question_id === chapterQuestions[1].id)}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmitAnswer={handleCourseAnswerSubmit}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onRetakeQuestion={handleQuestionRetake}
          question={chapterQuestions[1]}
        />
        <p className="text-2xl text-left font-bold mt-5 mb-10">
          In short, because everyone's too quick to agree without really considering other
          possibilities, they end up making a decision that might not be the best one.
        </p>
        <br />
        <p className="text-xl text-left font-bold mt-5 mb-10">Preventing Groupthink</p>
        <p className="text-lg text-left font-light mt-5 mb-10">
          To avoid groupthink, it's a good idea to have a system in place. Before making important
          and risky decisions, ask the quiet thinkers if they see any issues with the plan. Give
          them a chance to voice their concerns.
        </p>

        <p className="text-2xl text-center font-normal mt-5 mb-10">
          Working with others can be awesome, but sometimes it can get a bit tricky. Being part of a
          team means more than just showing up – it's about how you behave and interact with others.
          There are some behaviors that can really hurt the team, and it's important to know what
          they are so we can avoid them. We'll explore more about team behaviors to avoid next.
        </p>
        <button
          disabled={isPageDirty}
          onClick={handleNextChapterClick}
          className="btn-internal w-full"
        >
          Team Behaviors to Avoid
        </button>
      </div>
    </>
  );
};
